import React, { useCallback } from 'react';
import { Modal, ModalVariant, Button, Grid, GridItem, FormGroup, TextInput, Form, Card, CardTitle, CardBody, Alert } from '@patternfly/react-core';

import './customization-page.css';
import { hideFullRoiDetails, showFullRoiDetails, StoreState, updateRoiFmaId, updateRoiPhysicalProperty } from '../../store/reducers';
import { useDispatch, useSelector } from 'react-redux';
import { selectCustomizationValidationError, selectGlobalRoiCustomizationById, selectRoiCustomizationById } from '../../store/selectors';
import RoiItem from './RoiItem';
import { TableComposable, Tbody, Td, Thead, Tr } from '@patternfly/react-table';
import { columnIds, columnLangKeys } from './RoiTable';
import _ from 'lodash';
import { useTranslation } from 'react-i18next';
import PhysicalPropertiesDropdown from './PhysicalPropertiesDropdown';
import { CustomizationObjectType, PhysicalProperty } from '../../store/model-customization-types';

/** Models a full list of ROI IDs that the details modal can move through, and the current position (cursor) in it. */
export type FullRoiDetailsId = {
    currentRoiId: string;
    roiIdList: string[];
    isGlobalRoi: boolean;
}


const GRID_SPAN = 4;

const FullRoiDetailsModal = () => {
    const dispatch = useDispatch();
    const { t } = useTranslation();

    const fullRoiDetails = useSelector((state: StoreState) => state.fullRoiDetails);
    const isGlobalRoi = fullRoiDetails !== undefined && fullRoiDetails.isGlobalRoi === true;
    const validationError = useSelector((state: StoreState) => fullRoiDetails ? selectCustomizationValidationError(state, fullRoiDetails.currentRoiId) : undefined);

    const roi = useSelector((state: StoreState) => fullRoiDetails ? isGlobalRoi ?
        selectGlobalRoiCustomizationById(state, fullRoiDetails.currentRoiId) :
        selectRoiCustomizationById(state, fullRoiDetails.currentRoiId) :
        undefined);

    const handleClose = useCallback(() => {
        dispatch(hideFullRoiDetails());
    }, [dispatch]);

    const handlePreviousRoi = useCallback(() => {
        if (fullRoiDetails) {
            const { currentRoiId, roiIdList } = fullRoiDetails;
            const currentRoiListIndex = roiIdList.indexOf(currentRoiId);
            if (currentRoiListIndex > 0) {
                dispatch(showFullRoiDetails({ currentRoiId: roiIdList[currentRoiListIndex - 1], roiIdList: roiIdList, isGlobalRoi: isGlobalRoi }));
            }
        }
    }, [dispatch, fullRoiDetails, isGlobalRoi]);

    const handleNextRoi = useCallback(() => {
        if (fullRoiDetails) {
            const { currentRoiId, roiIdList } = fullRoiDetails;
            const currentRoiListIndex = roiIdList.indexOf(currentRoiId);
            if (currentRoiListIndex < roiIdList.length - 1) {
                dispatch(showFullRoiDetails({ currentRoiId: roiIdList[currentRoiListIndex + 1], roiIdList: roiIdList, isGlobalRoi: isGlobalRoi }));
            }
        }
    }, [dispatch, fullRoiDetails, isGlobalRoi]);

    const handleFmaCodeValueChanged = useCallback((value: string) => {
        if (roi) { dispatch(updateRoiFmaId(roi.id, { hasFmaId: true, fmaIdCodeValue: value }, isGlobalRoi)); }
    }, [dispatch, isGlobalRoi, roi]);

    const handleFmaCodeMeaningChanged = useCallback((value: string) => {
        if (roi) { dispatch(updateRoiFmaId(roi.id, { hasFmaId: true, fmaIdCodeMeaning: value }, isGlobalRoi)); }
    }, [dispatch, isGlobalRoi, roi]);

    const handleFmaCodeSchemeDesignatorChanged = useCallback((value: string) => {
        if (roi) { dispatch(updateRoiFmaId(roi.id, { hasFmaId: true, fmaIdCodingSchemeDesignator: value }, isGlobalRoi)); }
    }, [dispatch, isGlobalRoi, roi]);

    const handleFmaCodeSchemeVersionChanged = useCallback((value: string) => {
        if (roi) { dispatch(updateRoiFmaId(roi.id, { hasFmaId: true, fmaIdCodingSchemeVersion: value }, isGlobalRoi)); }
    }, [dispatch, isGlobalRoi, roi]);

    const handleFmaContextGroupVersionChanged = useCallback((value: string) => {
        if (roi) { dispatch(updateRoiFmaId(roi.id, { hasFmaId: true, fmaIdContextGroupVersion: value }, isGlobalRoi)); }
    }, [dispatch, isGlobalRoi, roi]);

    const handleFmaContextIdentifierChanged = useCallback((value: string) => {
        if (roi) { dispatch(updateRoiFmaId(roi.id, { hasFmaId: true, fmaIdContextIdentifier: value }, isGlobalRoi)); }
    }, [dispatch, isGlobalRoi, roi]);

    const handleFmaContextUidChanged = useCallback((value: string) => {
        if (roi) { dispatch(updateRoiFmaId(roi.id, { hasFmaId: true, fmaIdContextUid: value }, isGlobalRoi)); }
    }, [dispatch, isGlobalRoi, roi]);

    const handleFmaMappingResourceChanged = useCallback((value: string) => {
        if (roi) { dispatch(updateRoiFmaId(roi.id, { hasFmaId: true, fmaIdMappingResource: value }, isGlobalRoi)); }
    }, [dispatch, isGlobalRoi, roi]);

    const handleFmaMappingResourceNameChanged = useCallback((value: string) => {
        if (roi) { dispatch(updateRoiFmaId(roi.id, { hasFmaId: true, fmaIdMappingResourceName: value }, isGlobalRoi)); }
    }, [dispatch, isGlobalRoi, roi]);

    const handleFmaMappingResourceUidChanged = useCallback((value: string) => {
        if (roi) { dispatch(updateRoiFmaId(roi.id, { hasFmaId: true, fmaIdMappingResourceUid: value }, isGlobalRoi)); }
    }, [dispatch, isGlobalRoi, roi]);

    const handlePhysicalPropertyAttributeChanged = useCallback((value: PhysicalProperty) => {
        if (roi) { dispatch(updateRoiPhysicalProperty(roi.id, value, undefined, isGlobalRoi)); }
    }, [dispatch, isGlobalRoi, roi]);

    const handlePhysicalPropertyValueChanged = useCallback((value: string) => {
        if (roi) { dispatch(updateRoiPhysicalProperty(roi.id, undefined, value, isGlobalRoi)); }
    }, [dispatch, isGlobalRoi, roi]);

    const isOpen = fullRoiDetails !== undefined && roi !== undefined;

    const currentIndex = fullRoiDetails ? fullRoiDetails.roiIdList.indexOf(fullRoiDetails.currentRoiId) : -1;
    const hasNext = fullRoiDetails && fullRoiDetails.roiIdList[fullRoiDetails.roiIdList.length - 1] !== fullRoiDetails.currentRoiId;
    const hasPrevious = fullRoiDetails && fullRoiDetails.roiIdList[0] !== fullRoiDetails.currentRoiId;

    const coveredRoisLength = isGlobalRoi ? _.get(roi, 'coveredRois', ['']).length : -1;

    const hasPhysicalPropertiesValidationError = validationError && validationError.type === CustomizationObjectType.PhysicalProperties;

    return (
        <Modal
            className="full-roi-details-modal"
            isOpen={isOpen}
            onClose={handleClose}
            title={isGlobalRoi ? t('fullRoiDetails.title.global', { count: coveredRoisLength }) : t('fullRoiDetails.title.regular')}
            variant={ModalVariant.large}
            actions={[
                <Button key="prev" variant="secondary" onClick={handlePreviousRoi} isDisabled={!hasPrevious}>{t('fullRoiDetails.previous')}</Button>,
                <Button key="next" variant="secondary" onClick={handleNextRoi} isDisabled={!hasNext}>{t('fullRoiDetails.next')}</Button>,
                <span className="list-index">{currentIndex !== -1 && fullRoiDetails && (<span>{currentIndex + 1}/{fullRoiDetails.roiIdList.length}</span>)}</span>,
                <Button key="close" variant="secondary" onClick={handleClose}>{t('common.close')}</Button>,
            ]}
        >
            {roi && (
                <div>
                    <TableComposable variant='compact'>
                        <Thead>
                            <Tr>
                                <Td></Td>
                                <Td>{t(columnLangKeys[columnIds.name])}</Td>
                                <Td>{t(columnLangKeys[columnIds.isIncluded])}</Td>
                                <Td>{t(columnLangKeys[columnIds.color])}</Td>
                                <Td>{t(columnLangKeys[columnIds.operation])}</Td>
                                <Td>{t(columnLangKeys[columnIds.interpretedType])}</Td>
                            </Tr>
                        </Thead>
                        <Tbody>
                            <RoiItem roiId={roi.id} hideIdentificationCodeFields isGlobalRoi={isGlobalRoi} />
                        </Tbody>
                    </TableComposable>

                    <div className="identification-code-table">
                        <Form isHorizontal>
                            <Grid hasGutter>
                                <GridItem span={GRID_SPAN}>
                                    <FormGroup
                                        fieldId="field-roi-details-fmaid-1"
                                        label="Code Value">
                                        <TextInput
                                            type="text"
                                            id="field-roi-details-fmaid-1"
                                            onChange={handleFmaCodeValueChanged}
                                            value={roi.fmaIdCodeValue}
                                        />
                                    </FormGroup>
                                </GridItem>
                                <GridItem span={GRID_SPAN}>
                                    <FormGroup
                                        fieldId="field-roi-details-fmaid-2"
                                        label="Code Meaning">
                                        <TextInput
                                            type="text"
                                            id="field-roi-details-fmaid-2"
                                            onChange={handleFmaCodeMeaningChanged}
                                            value={roi.fmaIdCodeMeaning}
                                        />
                                    </FormGroup>
                                </GridItem>
                                <GridItem span={GRID_SPAN}>
                                    <FormGroup
                                        fieldId="field-roi-details-fmaid-3"
                                        label="Code Scheme Designator">
                                        <TextInput
                                            type="text"
                                            id="field-roi-details-fmaid-3"
                                            onChange={handleFmaCodeSchemeDesignatorChanged}
                                            value={roi.fmaIdCodingSchemeDesignator}
                                        />
                                    </FormGroup>
                                </GridItem>
                                <GridItem span={GRID_SPAN}>
                                    <FormGroup
                                        fieldId="field-roi-details-fmaid-4"
                                        label="Code Scheme Version">
                                        <TextInput
                                            type="text"
                                            id="field-roi-details-fmaid-4"
                                            onChange={handleFmaCodeSchemeVersionChanged}
                                            value={roi.fmaIdCodingSchemeVersion}
                                        />
                                    </FormGroup>
                                </GridItem>
                                <GridItem span={GRID_SPAN}>
                                    <FormGroup
                                        fieldId="field-roi-details-fmaid-5"
                                        label="Mapping Resource">
                                        <TextInput
                                            type="text"
                                            id="field-roi-details-fmaid-5"
                                            onChange={handleFmaMappingResourceChanged}
                                            value={roi.fmaIdMappingResource}
                                        />
                                    </FormGroup>
                                </GridItem>
                                <GridItem span={GRID_SPAN}>
                                    <FormGroup
                                        fieldId="field-roi-details-fmaid-6"
                                        label="Context Group Version">
                                        <TextInput
                                            type="text"
                                            id="field-roi-details-fmaid-6"
                                            onChange={handleFmaContextGroupVersionChanged}
                                            value={roi.fmaIdContextGroupVersion}
                                        />
                                    </FormGroup>
                                </GridItem>
                                <GridItem span={GRID_SPAN}>
                                    <FormGroup
                                        fieldId="field-roi-details-fmaid-7"
                                        label="Context Identifier">
                                        <TextInput
                                            type="text"
                                            id="field-roi-details-fmaid-7"
                                            onChange={handleFmaContextIdentifierChanged}
                                            value={roi.fmaIdContextIdentifier}
                                        />
                                    </FormGroup>
                                </GridItem>
                                <GridItem span={GRID_SPAN}>
                                    <FormGroup
                                        fieldId="field-roi-details-fmaid-8"
                                        label="Context UID">
                                        <TextInput
                                            type="text"
                                            id="field-roi-details-fmaid-8"
                                            onChange={handleFmaContextUidChanged}
                                            value={roi.fmaIdContextUid}
                                        />
                                    </FormGroup>
                                </GridItem>
                                <GridItem span={GRID_SPAN}>
                                    <FormGroup
                                        fieldId="field-roi-details-fmaid-9"
                                        label="Mapping Resource UID">
                                        <TextInput
                                            type="text"
                                            id="field-roi-details-fmaid-9"
                                            onChange={handleFmaMappingResourceUidChanged}
                                            value={roi.fmaIdMappingResourceUid}
                                        />
                                    </FormGroup>
                                </GridItem>
                                <GridItem span={5}>
                                    <FormGroup
                                        fieldId="field-roi-details-fmaid-10"
                                        label="Mapping Resource Name">
                                        <TextInput
                                            type="text"
                                            id="field-roi-details-fmaid-10"
                                            onChange={handleFmaMappingResourceNameChanged}
                                            value={roi.fmaIdMappingResourceName}
                                        />
                                    </FormGroup>
                                </GridItem>
                            </Grid>

                            <Card isFlat className="physical-properties-card-container">
                                <CardTitle>{t('fullRoiDetails.physicalProperties.box.title')}</CardTitle>
                                <CardBody className="physical-properties-card">
                                    <FormGroup label="Physical property" fieldId="field-roi-physical-property-attribute">
                                        <PhysicalPropertiesDropdown
                                            id="field-roi-physical-property-attribute"
                                            onSelect={handlePhysicalPropertyAttributeChanged}
                                            value={roi.physicalPropertyAttribute} />
                                    </FormGroup>
                                    <FormGroup
                                        label="Physical property value"
                                        fieldId="field-roi-physical-property-value">
                                        <TextInput
                                            type="text"
                                            id="field-roi-physical-property-value"
                                            onChange={handlePhysicalPropertyValueChanged}
                                            value={roi.physicalPropertyValue || ''}
                                        />
                                    </FormGroup>
                                    {hasPhysicalPropertiesValidationError &&
                                        (<div className="roi-item validation-error validation-error-box">
                                            <Alert variant="danger" isInline isPlain isExpandable title={`Validation error: ${validationError.message}`}>
                                                {validationError.field && (<div>Field: {validationError.field}</div>)}
                                                <div>Error type: {validationError.detail}</div>
                                                <div>Error ctx: {validationError.ctx}</div>
                                            </Alert>
                                        </div>)}
                                </CardBody>

                            </Card>

                        </Form>
                    </div>

                </div>
            )}
        </Modal>
    );
}

export default FullRoiDetailsModal;
